// The landing's sentence: "I loved Split in late June, around midday."
//
// The headline promises "find your kind of day", and this is the promise made a
// control. It is the map's own idiom — a sentence whose words are the controls,
// the choice with a solid underline and the adjustable parts dashed — turned
// round to ask the question instead of stating the answer.
//
// The three words edit myday.ts's draft day, which the recipe editor's seeder
// row also edits: one day, two views of it. Each word opens one picker, hung
// from that word — a place search, the part-and-month picker the map's "mid
// July" uses, or the four times of day — in a single surface, #dy-menu, rebuilt
// for whichever word asked.

import { $ } from '../dom.ts';
import { state, store } from '../state.ts';
import { sectionLabel } from '../../lib/sections.ts';
import { whenPicker, bucketPicker } from './sentence.ts';
import { getDraft, onDraftChange, setDraftCity, setDraftWhen, setDraftBucket, applyDay, pickPhoto } from '../myday.ts';
import { MONTH_NAMES } from '../../lib/sections.ts';
import type { MenuName } from '../state.ts';
import type { CityHit } from '../../contracts/city.ts';

export interface DaySentenceDeps {
  wireAutocomplete: (input: HTMLInputElement, box: HTMLElement, onPick: (s: CityHit) => void, opts?: { clearOnPick?: boolean }) => unknown;
  openSearch: () => void;
}

let deps: DaySentenceDeps;

const WORDS: [string, MenuName][] = [['dy-place', 'day-place'], ['dy-when', 'day-when'], ['dy-bucket', 'day-bucket']];
const lower1 = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);

// The place picker is built once and kept: its input carries the autocomplete's
// listeners, so it is re-parented into the menu rather than rebuilt each time.
let placeBox: HTMLElement | null = null;
let placeInput: HTMLInputElement | null = null;

function buildPlaceBox(): HTMLElement {
  const box = document.createElement('div');
  box.className = 'dy-place-box';
  const field = document.createElement('div');
  field.className = 'search-field dy-search';
  const input = document.createElement('input');
  input.type = 'text';
  input.autocomplete = 'off';
  input.spellcheck = false;
  input.placeholder = 'Where were you? Split, Lisbon…';
  input.setAttribute('aria-label', 'Where the day was');
  field.append(input);
  const sugg = document.createElement('div');
  sugg.className = 'suggestions dy-sugg';
  sugg.hidden = true;
  sugg.setAttribute('role', 'listbox');
  box.append(field, sugg);
  deps.wireAutocomplete(input, sugg, (s) => {
    setDraftCity(s);
    store.set({ menu: null });
  });
  placeInput = input;
  return box;
}

export function initDaySentence(d: DaySentenceDeps): void {
  deps = d;
}

// The words, from the draft. A word nobody has said yet shows what it asks —
// where, when, what time — muted, so the sentence reads as a form to fill in.
function word(id: string, text: string | null, blank: string): void {
  const el = $(id);
  el.textContent = text || blank;
  el.classList.toggle('dy-blank', !text);
}

export function renderDaySentence(): void {
  const d = getDraft();
  word('dy-place', d.city ? d.city.name : null, 'where');
  // A month without its part still says something true; a part alone does not.
  const when = d.dekad ? lower1(sectionLabel(d.dekad)) : d.month ? MONTH_NAMES[d.month - 1] : null;
  word('dy-when', when, 'when');
  const bk = d.bucket ? state.manifest?.buckets.find((b) => b.key === d.bucket) : null;
  word('dy-bucket', bk ? lower1(bk.label) : null, 'what time');
  $<HTMLButtonElement>('dy-go').disabled = !(d.city && d.dekad && d.bucket);
}

// Hang the picker from its word. It opens UPWARD when the word sits low on the
// screen — the landing's block is bottom-anchored, and a list of twelve months
// has no room below it there — and is bounded by whichever side it opens on.
function place(box: HTMLElement, word: HTMLElement): void {
  const r = word.getBoundingClientRect();
  const gap = 10;
  const below = window.innerHeight - r.bottom - gap - 16;
  const above = r.top - gap - 16;
  box.style.left = `${Math.max(16, Math.min(r.left - 14, window.innerWidth - box.offsetWidth - 16))}px`;
  if (below >= 320 || below >= above) {
    box.style.top = `${r.bottom + gap}px`;
    box.style.bottom = '';
    box.style.maxHeight = `${below}px`;
  } else {
    box.style.top = '';
    box.style.bottom = `${window.innerHeight - r.top + gap}px`;
    box.style.maxHeight = `${above}px`;
  }
}

// state.menu's render for the three words.
export function renderDayMenu(): void {
  const m = state.menu;
  const box = $('dy-menu');
  for (const [id, name] of WORDS) {
    const el = $(id);
    el.classList.toggle('open', m === name);
    el.setAttribute('aria-expanded', String(m === name));
  }
  if (m !== 'day-place' && m !== 'day-when' && m !== 'day-bucket') {
    box.hidden = true;
    return;
  }
  const d = getDraft();
  if (m === 'day-place') {
    if (!placeBox) placeBox = buildPlaceBox();
    box.replaceChildren(placeBox);
  } else if (m === 'day-when') {
    const pick = (month: number, part: number) => {
      setDraftWhen(month, part);
      // Stays open until both are said: month then part is one errand.
      if (month && part) store.set({ menu: null });
      else renderDayMenu();
    };
    box.replaceChildren(...whenPicker({ month: d.month ?? 0, part: d.part ?? 0 }, pick));
  } else {
    box.replaceChildren(bucketPicker(d.bucket ?? '', (key) => { setDraftBucket(key); store.set({ menu: null }); }));
  }
  box.classList.toggle('dy-menu-place', m === 'day-place');
  box.hidden = false;
  const word = $(WORDS.find(([, n]) => n === m)![0]);
  place(box, word);
  if (m === 'day-place' && placeInput) {
    placeInput.value = '';
    placeInput.focus();
  }
}

export function wireDaySentence(): void {
  for (const [id, name] of WORDS) {
    $(id).addEventListener('click', (e) => {
      e.stopPropagation(); // the document closer would undo this click
      store.set({ menu: state.menu === name ? null : name });
    });
  }
  $('dy-go').addEventListener('click', () => { applyDay(); });
  $('dy-photo').addEventListener('click', () => pickPhoto());
  $('landing-search').addEventListener('click', (e) => { e.stopPropagation(); deps.openSearch(); });
  onDraftChange(renderDaySentence);
  window.addEventListener('resize', () => { if (String(state.menu).startsWith('day-')) renderDayMenu(); });
  renderDaySentence();
}
